import { properties, payments, maintenance, dashboard } from '../api';
import { createPropertyCard } from '../components/card';
import { createPageHeader } from '../components/form';
import { createDataTable, type Column } from '../components/table';
import { navigate } from '../router';
import type { Property, PaymentTimeline, MaintenanceIssue, ChartData } from '../types';

function fmt(n: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
}

export async function propertiesPage(): Promise<HTMLElement> {
  const page = document.createElement('div');

  const headerActions = document.createElement('div');
  headerActions.className = 'flex gap-2';
  headerActions.innerHTML = `
    <a href="#/properties/new" class="btn-primary">+ Add Property</a>
  `;
  page.appendChild(createPageHeader('Properties', headerActions));

  let list: Property[];
  try {
    list = await properties.list();
  } catch {
    page.innerHTML += '<div class="text-red-500 p-4">Failed to load properties.</div>';
    return page;
  }

  if (list.length === 0) {
    page.innerHTML += `
      <div class="text-center py-16">
        <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
        <h3 class="text-lg font-medium text-gray-500">No properties yet</h3>
        <p class="text-gray-400 mb-6">Add your first investment property to get started.</p>
        <a href="#/properties/new" class="btn-primary">Add Property</a>
      </div>
    `;
    return page;
  }

  // View toggle and search
  const toolbar = document.createElement('div');
  toolbar.className = 'flex items-center gap-4 mb-6';
  toolbar.innerHTML = `
    <input type="text" id="prop-search" class="input-field max-w-md flex-1" placeholder="Search properties...">
    <div class="flex rounded-lg border border-gray-200 overflow-hidden">
      <button class="px-3 py-1.5 text-sm font-medium view-btn text-gray-500 hover:bg-gray-50" data-view="cards">Cards</button>
      <button class="px-3 py-1.5 text-sm font-medium view-btn bg-primary-50 text-primary-700" data-view="table">Table</button>
      <button class="px-3 py-1.5 text-sm font-medium view-btn text-gray-500 hover:bg-gray-50" data-view="timeline">Timeline</button>
    </div>
  `;
  page.appendChild(toolbar);

  const content = document.createElement('div');
  page.appendChild(content);

  let currentView = 'table';
  let timeline: PaymentTimeline[] | null = null;
  let allMaintenance: MaintenanceIssue[] | null = null;
  let chartData: ChartData | null = null;
  let filteredList = list;

  const renderView = async () => {
    content.innerHTML = '';
    if (currentView === 'cards') {
      const grid = document.createElement('div');
      grid.className = 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4';
      filteredList.forEach(p => grid.appendChild(createPropertyCard(p)));
      content.appendChild(grid);
    } else if (currentView === 'table') {
      // Load timeline + maintenance + expenses in parallel if not yet loaded
      if (!timeline || !allMaintenance || !chartData) {
        content.innerHTML = '<div class="text-center py-8 text-gray-400">Loading data...</div>';
        try {
          const [t, m, c] = await Promise.all([
            timeline ?? payments.timeline(3),
            allMaintenance ?? maintenance.all(),
            chartData ?? dashboard.chart(1),
          ]);
          timeline = t;
          allMaintenance = m;
          chartData = c;
        } catch {
          content.innerHTML = '<div class="text-red-500 p-4">Failed to load table data.</div>';
          return;
        }
        content.innerHTML = '';
      }

      // Build lookup maps from loaded data
      const receivedMap = new Map<string, number>();
      for (const t of timeline!) {
        const lastMonth = t.months[t.months.length - 1];
        if (lastMonth) receivedMap.set(t.property_id, lastMonth.actual);
      }
      const openMaintMap = new Map<string, number>();
      for (const m of allMaintenance!) {
        if (m.status === 'open' || m.status === 'in-progress') {
          openMaintMap.set(m.property_id, (openMaintMap.get(m.property_id) ?? 0) + 1);
        }
      }

      // Per-property actual expenses (annual / 12)
      const expenseMap = chartData!.per_property_expenses_annual ?? {};

      const cols: Column<Property>[] = [
        { label: 'Address', render: r => `<a href="#/properties/${r.id}" class="text-primary-600 hover:underline font-medium">${r.address}</a>` },
        { label: 'Suburb', key: 'suburb' },
        { label: 'Exp./mo', render: r => fmt(r.weekly_rent * 52 / 12), className: 'text-right' },
        { label: 'Rcvd/mo', render: r => { const v = receivedMap.get(r.id); return v !== undefined ? fmt(v) : '—'; }, className: 'text-right' },
        {
          label: 'Costs/mo', render: r => {
            const annual = expenseMap[r.id] ?? 0;
            const mgmtMo = r.weekly_rent * 52 / 12 * (r.management_fee_percent ?? 0) / 100;
            const total = annual / 12 + mgmtMo;
            return fmt(total);
          }, className: 'text-right'
        },

        {
          label: 'Lease End', render: r => {
            if (!r.lease_end_date) return '—';
            const days = Math.ceil((new Date(r.lease_end_date).getTime() - Date.now()) / 86400000);
            const color = days < 0 ? 'red' : days < 80 ? 'yellow' : 'green';
            return `<span class="badge badge-${color}">${r.lease_end_date} (${days}d)</span>`;
          }
        },
        {
          label: 'Maint.', render: r => {
            const count = openMaintMap.get(r.id) ?? 0;
            if (count === 0) return '<span class="text-gray-400">—</span>';
            return `<a href="#/maintenance?property_id=${r.id}" class="badge badge-yellow">${count} open</a>`;
          }
        },
        {
          label: 'Alerts', render: r => {
            const flags: string[] = [];
            const today = Date.now();
            const warn = 60 * 86400000; // 60 days
            const crit = 14 * 86400000; // 14 days
            const checks: { label: string; date: string | undefined }[] = [
              { label: 'Ins', date: r.insurance_renewal_date },
              { label: 'Gas', date: r.gas_safety_expiry },
              { label: 'Elec', date: r.electrical_safety_expiry },
              { label: 'Smoke', date: r.smoke_alarm_expiry },
            ];
            for (const c of checks) {
              if (!c.date) continue;
              const diff = new Date(c.date).getTime() - today;
              if (diff < 0) flags.push(`<span class="badge badge-red" title="${c.label} expired">${c.label}</span>`);
              else if (diff < crit) flags.push(`<span class="badge badge-red" title="${c.label} in ${Math.ceil(diff/86400000)}d">${c.label}</span>`);
              else if (diff < warn) flags.push(`<span class="badge badge-yellow" title="${c.label} in ${Math.ceil(diff/86400000)}d">${c.label}</span>`);
            }
            return flags.length > 0 ? `<div class="flex flex-wrap gap-1">${flags.join('')}</div>` : '<span class="text-gray-400">—</span>';
          }
        },
      ];
      content.appendChild(createDataTable(cols, filteredList, {
        emptyText: 'No properties match your search.',
        onRowClick: (row) => navigate(`#/properties/${row.id}`),
      }));
    } else if (currentView === 'timeline') {
      // Load timeline data on first view
      if (!timeline) {
        content.innerHTML = '<div class="text-center py-8 text-gray-400">Loading payment timeline...</div>';
        try {
          timeline = await payments.timeline(6);
        } catch {
          content.innerHTML = '<div class="text-red-500 p-4">Failed to load payment timeline.</div>';
          return;
        }
        content.innerHTML = '';
      }
      renderTimeline(content, timeline, filteredList);
    }
  };

  // Search
  toolbar.querySelector('#prop-search')!.addEventListener('input', (e) => {
    const q = (e.target as HTMLInputElement).value.toLowerCase();
    filteredList = list.filter(p =>
      p.address.toLowerCase().includes(q) ||
      p.suburb.toLowerCase().includes(q) ||
      p.tenant_name?.toLowerCase().includes(q)
    );
    renderView();
  });

  // View toggle
  toolbar.querySelectorAll('.view-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      toolbar.querySelectorAll('.view-btn').forEach(b => {
        b.classList.remove('bg-primary-50', 'text-primary-700');
        b.classList.add('text-gray-500');
      });
      btn.classList.add('bg-primary-50', 'text-primary-700');
      btn.classList.remove('text-gray-500');
      currentView = btn.getAttribute('data-view')!;
      renderView();
    });
  });

  await renderView();
  return page;
}

function renderTimeline(container: HTMLElement, timeline: PaymentTimeline[], filteredList: Property[]): void {
  if (timeline.length === 0) {
    container.innerHTML = '<div class="text-center py-8 text-gray-400">No payment timeline data available.</div>';
    return;
  }

  const filteredIds = new Set(filteredList.map(p => p.id));
  const filtered = timeline.filter(t => filteredIds.has(t.property_id));

  // Get month headers from first property
  const months = filtered[0]?.months?.map(m => m.month) || [];

  const wrapper = document.createElement('div');
  wrapper.className = 'overflow-x-auto';

  const table = document.createElement('table');
  table.className = 'data-table';

  // Header
  const thead = document.createElement('thead');
  thead.innerHTML = `<tr>
    <th>Property</th>
    <th class="text-right">Rent/wk</th>
    ${months.map(m => `<th class="text-center">${m}</th>`).join('')}
  </tr>`;
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  filtered.forEach(t => {
    const tr = document.createElement('tr');
    tr.className = 'cursor-pointer hover:bg-gray-50';
    tr.addEventListener('click', () => navigate(`#/properties/${t.property_id}`));
    tr.innerHTML = `
      <td class="font-medium"><a href="#/properties/${t.property_id}" class="text-primary-600 hover:underline">${t.address}</a></td>
      <td class="text-right">${fmt(t.weekly_rent)}</td>
      ${t.months.map(m => {
      const bgColor = m.color === 'green' ? 'bg-green-50 text-green-700' :
        m.color === 'amber' ? 'bg-amber-50 text-amber-700' :
          m.color === 'red' ? 'bg-red-50 text-red-700' : '';
      return `<td class="text-center ${bgColor}">
          <div class="text-sm font-medium">${fmt(m.actual)}</div>
          <div class="text-xs text-gray-400">${fmt(m.expected)}</div>
        </td>`;
    }).join('')}
    `;
    tbody.appendChild(tr);
  });
  table.appendChild(tbody);
  wrapper.appendChild(table);

  // Legend
  const legend = document.createElement('div');
  legend.className = 'flex gap-4 mt-3 text-xs text-gray-500';
  legend.innerHTML = `
    <span class="flex items-center gap-1"><span class="w-3 h-3 rounded bg-green-100 border border-green-300"></span> On track</span>
    <span class="flex items-center gap-1"><span class="w-3 h-3 rounded bg-amber-100 border border-amber-300"></span> Partial</span>
    <span class="flex items-center gap-1"><span class="w-3 h-3 rounded bg-red-100 border border-red-300"></span> Missed</span>
    <span class="text-gray-400">Top: actual | Bottom: expected</span>
  `;

  container.appendChild(wrapper);
  container.appendChild(legend);
}
